/**
 * Admin Panel > Workspaces: Core's standard table (O57).
 *
 * The page loads every workspace ONCE (`GET /auth/admin/workspaces?all=true`) and does everything
 * else in the browser: instant strict fuzzy search (name, website, owner e-mail), sortable columns,
 * filters, client-side pagination, multi-select that survives search/sort/filter/paging, and bulk
 * actions (Set package, Set tier, Set client status, Export CSV) that run the existing per-workspace writes one at a
 * time with a per-row result. "Strict" fuzzy (no typo or letters-in-order matches) because the
 * selection feeds writes.
 */
import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, ArrowUpDown, Building2, Plus, Search, Settings2 } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { TablePagination, usePagination, useTableSort } from '@/components/ui/data-table';
import { fuzzyFilter } from '@/lib/fuzzyMatch';
import { apiClient } from '@/services/apiClient';
import { fetchAdminWorkspaces, setTenantPackage, setTenantTier } from '@/services/authApi';
import type { AdminWorkspace } from '@/services/authApi';
import { NewWorkspaceDialog } from './NewWorkspaceDialog';
import { WorkspaceManageDrawer } from './WorkspaceManageDrawer';

const QUERY_KEY = ['admin', 'workspaces'] as const;

const PACKAGES = [
  { value: 'seo', label: 'SEO' },
  { value: 'ads', label: 'Ads' },
  { value: 'both', label: 'Both' },
] as const;
const TIERS = [
  { value: 1, label: 'DTC' },
  { value: 2, label: 'B2B' },
  { value: 3, label: 'Enterprise' },
] as const;
const CLIENT_STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'offboarded', label: 'Offboarded' },
] as const;
const WORKSPACE_STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
] as const;

const packageLabel = (v: string | null | undefined) => PACKAGES.find((p) => p.value === v)?.label ?? null;
const tierLabel = (v: number | null | undefined) => TIERS.find((t) => t.value === v)?.label ?? null;
const clientStatusLabel = (v: string | null | undefined) => CLIENT_STATUSES.find((s) => s.value === v)?.label ?? null;
const isoDay = (iso: string | null) => (iso ? iso.slice(0, 10) : null);

type SortKey =
  | 'name' | 'website' | 'owner' | 'members' | 'package' | 'tier' | 'client_status' | 'workspace_status' | 'created';

const SORT_ACCESSORS = {
  name: (w: AdminWorkspace) => w.name.toLowerCase(),
  website: (w: AdminWorkspace) => w.website?.toLowerCase() ?? null,
  owner: (w: AdminWorkspace) => w.owner_email?.toLowerCase() ?? null,
  members: (w: AdminWorkspace) => w.member_count,
  package: (w: AdminWorkspace) => w.product_package ?? null,
  tier: (w: AdminWorkspace) => w.google_ads_product_tier ?? null,
  client_status: (w: AdminWorkspace) => w.client_status ?? null,
  workspace_status: (w: AdminWorkspace) => (w.is_active ? 'active' : 'inactive'),
  created: (w: AdminWorkspace) => w.created_at ?? null,
} satisfies Record<SortKey, (w: AdminWorkspace) => string | number | null>;

function csvCell(value: string | number | null | undefined): string {
  let s = value === null || value === undefined ? '' : String(value);
  // Spreadsheet formula injection: a text cell that starts with = + - @ TAB or CR would be run as a
  // formula when the file is opened (names and owner e-mails are user-controlled). A leading apostrophe
  // makes it plain text. Numbers are not text cells and are written as they are.
  if (typeof value === 'string' && /^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** The visible columns of the selected rows as RFC 4180 CSV (CRLF line ends, UTF-8 BOM in the file). */
function workspacesCsv(rows: AdminWorkspace[]): string {
  const header = ['Name', 'Website', 'Owner', 'Members', 'Package', 'Tier', 'Client status', 'Workspace status', 'Created'];
  const lines = rows.map((w) => [
    w.name, w.website, w.owner_email, w.member_count, packageLabel(w.product_package),
    tierLabel(w.google_ads_product_tier), clientStatusLabel(w.client_status),
    w.is_active ? 'Active' : 'Inactive', isoDay(w.created_at),
  ].map(csvCell).join(','));
  return [header.join(','), ...lines].join('\r\n') + '\r\n';
}

function downloadCsv(rows: AdminWorkspace[]) {
  const blob = new Blob(['﻿' + workspacesCsv(rows)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `workspaces-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function ClientStatusBadge({ status }: { status: string | null | undefined }) {
  const label = clientStatusLabel(status);
  if (!label) return <span className="text-muted-foreground text-sm">—</span>;
  const tone = status === 'active'
    ? 'bg-teal-light text-teal-dark border-teal/20'
    : status === 'paused'
      ? 'bg-muted text-muted-foreground border-border'
      : 'bg-destructive/10 text-destructive border-destructive/20';
  return <Badge variant="outline" className={tone}>{label}</Badge>;
}

function WorkspaceStatusBadge({ active }: { active: boolean }) {
  return active
    ? <Badge variant="outline" className="bg-teal-light text-teal-dark border-teal/20">Active</Badge>
    : <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/20">Inactive</Badge>;
}

function SortHead({
  label, sortKey, currentKey, dir, onSort, className,
}: {
  label: string; sortKey: SortKey; currentKey: string | null; dir: 'asc' | 'desc';
  onSort: (key: SortKey) => void; className?: string;
}) {
  const active = currentKey === sortKey;
  return (
    <TableHead
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={`cursor-pointer select-none whitespace-nowrap hover:text-foreground ${className ?? ''}`}
      onClick={() => onSort(sortKey)}
    >
      <button
        type="button"
        className="inline-flex items-center gap-1"
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSort(sortKey); } }}
      >
        {label}
        {active
          ? (dir === 'asc' ? <ArrowUp className="h-3 w-3 text-teal" /> : <ArrowDown className="h-3 w-3 text-teal" />)
          : <ArrowUpDown className="h-3 w-3 text-muted-foreground/50" />}
      </button>
    </TableHead>
  );
}

type BulkKind = 'package' | 'tier' | 'status';

interface RowResult { id: string; name: string; outcome: 'done' | 'failed' | 'skipped'; detail?: string }

const FIRST_NAMES = 5;

export function WorkspacesTable() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: [...QUERY_KEY, 'all'],
    queryFn: () => fetchAdminWorkspaces({ all: true }),
  });

  const [search, setSearch] = useState('');
  const [fPackage, setFPackage] = useState('all');
  const [fTier, setFTier] = useState('all');
  const [fClient, setFClient] = useState('all');
  const [fWorkspace, setFWorkspace] = useState('all');
  const { sortKey, sortDir, toggleSort, sortRows } = useTableSort<AdminWorkspace>();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [newOpen, setNewOpen] = useState(false);
  const [managingId, setManagingId] = useState<string | null>(null);

  const all = useMemo(() => data?.workspaces ?? [], [data]);

  const filtered = useMemo(() => {
    let rows = all;
    if (fPackage !== 'all') rows = rows.filter((w) => w.product_package === fPackage);
    if (fTier !== 'all') rows = rows.filter((w) => String(w.google_ads_product_tier ?? '') === fTier);
    if (fClient !== 'all') rows = rows.filter((w) => w.client_status === fClient);
    if (fWorkspace !== 'all') rows = rows.filter((w) => (w.is_active ? 'active' : 'inactive') === fWorkspace);
    rows = fuzzyFilter(rows, search, (w) => [w.name, w.website, w.owner_email], { strict: true, sort: sortKey === null });
    return sortRows(rows, SORT_ACCESSORS);
  }, [all, fPackage, fTier, fClient, fWorkspace, search, sortKey, sortRows]);

  const pagination = usePagination(filtered.length, { defaultPageSize: 20 });
  const pageRows = pagination.slice(filtered);

  const selectedRows = useMemo(() => all.filter((w) => selected.has(w.id)), [all, selected]);
  const allFilteredSelected = filtered.length > 0 && filtered.every((w) => selected.has(w.id));

  function toggleRow(id: string, on: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id); else next.delete(id);
      return next;
    });
  }
  function toggleAllFiltered(on: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const w of filtered) { if (on) next.add(w.id); else next.delete(w.id); }
      return next;
    });
  }
  const clearSelection = () => setSelected(new Set());
  const resetPage = () => pagination.setPage(1);

  // ---- per-row controls (unchanged behaviour) ----
  const packageMutation = useMutation({
    mutationFn: ({ tenantId, pkg }: { tenantId: string; pkg: 'seo' | 'ads' | 'both' }) => setTenantPackage(tenantId, pkg),
    onSuccess: (result) => {
      toast.success(`Package updated: ${result.product_package}`);
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err: unknown) => toast.error(err instanceof Error ? err.message : 'Failed to update package'),
  });
  const tierMutation = useMutation({
    mutationFn: ({ tenantId, tier }: { tenantId: string; tier: number }) => setTenantTier(tenantId, tier),
    onSuccess: (result) => {
      toast.success(`Tier updated: ${result.product_tier}`);
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err: unknown) => toast.error(err instanceof Error ? err.message : 'Failed to update tier'),
  });

  // ---- bulk actions ----
  const [bulk, setBulk] = useState<BulkKind | null>(null);
  const [choice, setChoice] = useState('');
  const [understood, setUnderstood] = useState(false);
  const [run, setRun] = useState<{ total: number; done: number } | null>(null);
  const [results, setResults] = useState<RowResult[] | null>(null);
  const running = useRef(false);

  function openBulk(kind: BulkKind) {
    setBulk(kind);
    setChoice('');
    setUnderstood(false);
    setResults(null);
  }

  const writable = selectedRows.filter((w) => w.website);
  const skipped = selectedRows.filter((w) => !w.website);

  async function applyBulk() {
    if (!bulk || !choice || running.current) return;
    if (bulk === 'status' && needsConfirm && !understood) return;
    running.current = true;
    const kind = bulk;
    const value = choice;
    const rows = selectedRows;
    setBulk(null);
    setResults(null);
    setRun({ total: rows.filter((w) => w.website).length, done: 0 });
    const out: RowResult[] = [];
    let done = 0;
    for (const w of rows) {
      if (!w.website) {
        out.push({ id: w.id, name: w.name, outcome: 'skipped', detail: 'no website' });
        continue;
      }
      try {
        if (kind === 'package') await setTenantPackage(w.website, value as 'seo' | 'ads' | 'both');
        else if (kind === 'tier') await setTenantTier(w.website, Number(value));
        else await apiClient.patch(`/tenant-settings/${encodeURIComponent(w.website)}/status`, { status: value });
        out.push({ id: w.id, name: w.name, outcome: 'done' });
      } catch (err) {
        out.push({ id: w.id, name: w.name, outcome: 'failed', detail: errorReason(err) });
      }
      done += 1;
      setRun({ total: rows.filter((r) => r.website).length, done });
    }
    running.current = false;
    setRun(null);
    setResults(out);
    clearSelection();
    await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
  }

  const bulkChoices = bulk === 'package'
    ? PACKAGES.map((p) => ({ value: p.value as string, label: p.label }))
    : bulk === 'status'
      ? CLIENT_STATUSES.map((s) => ({ value: s.value as string, label: s.label }))
      : TIERS.map((t) => ({ value: String(t.value), label: t.label }));
  const bulkTitle = bulk === 'package' ? 'Set package' : bulk === 'status' ? 'Set client status' : 'Set tier';
  // Pausing or offboarding stops ads and pacing work, so it needs an explicit tick.
  const needsConfirm = bulk === 'status' && (choice === 'paused' || choice === 'offboarded');

  const filtersActive = fPackage !== 'all' || fTier !== 'all' || fClient !== 'all' || fWorkspace !== 'all' || search.trim() !== '';

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-teal" />
              All Workspaces
            </CardTitle>
            <CardDescription>
              {data ? `${data.total} total workspaces` : 'Loading...'}
            </CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={() => setNewOpen(true)}>
              <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
              New workspace
            </Button>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <Input
              aria-label="Search workspaces"
              placeholder="Search name, website or owner…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); resetPage(); }}
              className="w-72 pl-8"
            />
          </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 pt-3">
          <FilterSelect label="Package" value={fPackage} onChange={(v) => { setFPackage(v); resetPage(); }}
            options={PACKAGES.map((p) => ({ value: p.value, label: p.label }))} />
          <FilterSelect label="Tier" value={fTier} onChange={(v) => { setFTier(v); resetPage(); }}
            options={TIERS.map((t) => ({ value: String(t.value), label: t.label }))} />
          <FilterSelect label="Client status" value={fClient} onChange={(v) => { setFClient(v); resetPage(); }}
            options={CLIENT_STATUSES.map((s) => ({ value: s.value, label: s.label }))} />
          <FilterSelect label="Workspace status" value={fWorkspace} onChange={(v) => { setFWorkspace(v); resetPage(); }}
            options={WORKSPACE_STATUSES.map((s) => ({ value: s.value, label: s.label }))} />
          {filtersActive && (
            <Button
              variant="ghost" size="sm"
              onClick={() => { setSearch(''); setFPackage('all'); setFTier('all'); setFClient('all'); setFWorkspace('all'); resetPage(); }}
            >
              Reset filters
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading && <p className="text-sm text-muted-foreground py-4">Loading workspaces...</p>}
        {isError && <p className="text-sm text-destructive py-4">Failed to load workspaces.</p>}
        {data?.truncated && (
          <p className="text-sm text-muted-foreground">Showing the first 5000 workspaces; narrow it down in the Audit Log or ask for the rest.</p>
        )}

        {selected.size > 0 && (
          <div role="toolbar" aria-label="Bulk actions" className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2">
            <span className="text-sm font-medium tabular-nums">{selected.size} selected</span>
            <Button size="sm" variant="outline" onClick={() => openBulk('package')}>Set package</Button>
            <Button size="sm" variant="outline" onClick={() => openBulk('tier')}>Set tier</Button>
            <Button size="sm" variant="outline" onClick={() => openBulk('status')}>Set client status</Button>
            <Button size="sm" variant="outline" onClick={() => downloadCsv(selectedRows)}>Export CSV</Button>
            <Button size="sm" variant="ghost" onClick={clearSelection}>Clear selection</Button>
          </div>
        )}

        {run && (
          <div className="space-y-1" role="status">
            <div
              role="progressbar"
              aria-label="Bulk update progress"
              aria-valuemin={0}
              aria-valuemax={run.total}
              aria-valuenow={run.done}
              className="h-2 w-full overflow-hidden rounded-full bg-muted"
            >
              <div className="h-full bg-primary transition-all motion-reduce:transition-none" style={{ width: `${run.total ? (run.done / run.total) * 100 : 0}%` }} />
            </div>
            <p className="text-xs text-muted-foreground tabular-nums">{run.done} of {run.total} done</p>
          </div>
        )}

        {results && (
          <section role="region" aria-label="Bulk result" className="rounded-md border border-border p-3">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold">Bulk result</h3>
              <Button size="sm" variant="ghost" onClick={() => setResults(null)}>Dismiss</Button>
            </div>
            <ul className="space-y-1 text-sm">
              {results.map((r) => (
                <li key={r.id}>
                  <span className="font-medium">{r.name}</span>{' '}
                  {r.outcome === 'done' && <span className="text-muted-foreground">Done</span>}
                  {r.outcome === 'failed' && <span className="text-destructive">Failed: {r.detail}</span>}
                  {r.outcome === 'skipped' && <span className="text-muted-foreground">Skipped: {r.detail}</span>}
                </li>
              ))}
            </ul>
          </section>
        )}

        {data && (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      aria-label="Select all filtered rows"
                      checked={allFilteredSelected}
                      onCheckedChange={(v) => toggleAllFiltered(v === true)}
                    />
                  </TableHead>
                  <SortHead label="Name" sortKey="name" currentKey={sortKey} dir={sortDir} onSort={toggleSort} />
                  <SortHead label="Website" sortKey="website" currentKey={sortKey} dir={sortDir} onSort={toggleSort} />
                  <SortHead label="Owner" sortKey="owner" currentKey={sortKey} dir={sortDir} onSort={toggleSort} />
                  <SortHead label="Members" sortKey="members" currentKey={sortKey} dir={sortDir} onSort={toggleSort} className="text-center" />
                  <SortHead label="Package" sortKey="package" currentKey={sortKey} dir={sortDir} onSort={toggleSort} />
                  <SortHead label="Tier" sortKey="tier" currentKey={sortKey} dir={sortDir} onSort={toggleSort} />
                  <SortHead label="Client status" sortKey="client_status" currentKey={sortKey} dir={sortDir} onSort={toggleSort} />
                  <SortHead label="Workspace status" sortKey="workspace_status" currentKey={sortKey} dir={sortDir} onSort={toggleSort} />
                  <SortHead label="Created" sortKey="created" currentKey={sortKey} dir={sortDir} onSort={toggleSort} />
                  <TableHead className="w-24"><span className="sr-only">Actions</span></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={11} className="py-8 text-center text-muted-foreground">
                      {all.length === 0 ? 'No workspaces found.' : 'No workspaces match your search or filters.'}
                    </TableCell>
                  </TableRow>
                )}
                {pageRows.map((ws) => (
                  <TableRow key={ws.id} data-state={selected.has(ws.id) ? 'selected' : undefined}>
                    <TableCell>
                      <Checkbox
                        aria-label={`Select ${ws.name}`}
                        checked={selected.has(ws.id)}
                        onCheckedChange={(v) => toggleRow(ws.id, v === true)}
                      />
                    </TableCell>
                    <TableCell className="font-medium"><span data-testid="workspace-name">{ws.name}</span></TableCell>
                    <TableCell className="text-sm text-muted-foreground">{ws.website || '—'}</TableCell>
                    <TableCell className="text-sm">{ws.owner_email || <span className="text-muted-foreground">No owner</span>}</TableCell>
                    <TableCell className="text-center">{ws.member_count}</TableCell>
                    <TableCell>
                      {ws.website ? (
                        <Select
                          value={ws.product_package ?? 'both'}
                          onValueChange={(val) => {
                            if (val !== (ws.product_package ?? 'both')) {
                              packageMutation.mutate({ tenantId: ws.website as string, pkg: val as 'seo' | 'ads' | 'both' });
                            }
                          }}
                          disabled={packageMutation.isPending}
                        >
                          <SelectTrigger aria-label={`Package for ${ws.name}`} className="h-7 w-24 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {PACKAGES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="text-sm text-muted-foreground">{packageLabel(ws.product_package) ?? '—'}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {ws.website ? (
                        <Select
                          value={String(ws.google_ads_product_tier ?? 1)}
                          onValueChange={(val) => {
                            if (Number(val) !== (ws.google_ads_product_tier ?? 1)) {
                              tierMutation.mutate({ tenantId: ws.website as string, tier: Number(val) });
                            }
                          }}
                          disabled={tierMutation.isPending}
                        >
                          <SelectTrigger aria-label={`Tier for ${ws.name}`} className="h-7 w-28 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {TIERS.map((t) => <SelectItem key={t.value} value={String(t.value)}>{t.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="text-sm text-muted-foreground">{tierLabel(ws.google_ads_product_tier) ?? '—'}</span>
                      )}
                    </TableCell>
                    <TableCell><ClientStatusBadge status={ws.website ? ws.client_status : null} /></TableCell>
                    <TableCell><WorkspaceStatusBadge active={ws.is_active} /></TableCell>
                    <TableCell className="text-sm text-muted-foreground">{isoDay(ws.created_at) ?? '—'}</TableCell>
                    <TableCell>
                      <Button size="sm" variant="outline" aria-label={`Manage ${ws.name}`} onClick={() => setManagingId(ws.id)}>
                        <Settings2 className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                        Manage
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              page={pagination.page}
              pageCount={pagination.pageCount}
              pageSize={pagination.pageSize}
              totalRows={filtered.length}
              onPageChange={pagination.setPage}
              onPageSizeChange={pagination.setPageSize}
            />
          </>
        )}
      </CardContent>

      <NewWorkspaceDialog open={newOpen} onClose={() => setNewOpen(false)} />
      <WorkspaceManageDrawer
        workspace={all.find((w) => w.id === managingId) ?? null}
        onClose={() => setManagingId(null)}
      />

      <Dialog open={bulk !== null} onOpenChange={(open) => { if (!open) setBulk(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{bulkTitle}</DialogTitle>
            <DialogDescription>
              {selectedRows.length} {selectedRows.length === 1 ? 'workspace' : 'workspaces'} selected.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>
              {writable.slice(0, FIRST_NAMES).map((w) => w.name).join(', ')}
              {writable.length > FIRST_NAMES ? ` and ${writable.length - FIRST_NAMES} more` : ''}
            </p>
            {skipped.length > 0 && (
              <p className="text-muted-foreground">
                Skipped, no website: {skipped.slice(0, FIRST_NAMES).map((w) => w.name).join(', ')}
                {skipped.length > FIRST_NAMES ? ` and ${skipped.length - FIRST_NAMES} more` : ''}
              </p>
            )}
            <RadioGroup value={choice} onValueChange={setChoice} aria-label={bulkTitle}>
              {bulkChoices.map((c) => (
                <div key={c.value} className="flex items-center gap-2">
                  <RadioGroupItem value={c.value} id={`bulk-${c.value}`} />
                  <Label htmlFor={`bulk-${c.value}`}>{c.label}</Label>
                </div>
              ))}
            </RadioGroup>
            {needsConfirm && (
              <div className="space-y-2 rounded-md border border-destructive/30 bg-destructive/5 p-3">
                <p className="text-destructive">
                  This stops ads and pacing work for {writable.length} {writable.length === 1 ? 'client' : 'clients'}.
                </p>
                <div className="flex items-center gap-2">
                  <Checkbox id="bulk-understood" checked={understood} onCheckedChange={(v) => setUnderstood(v === true)} />
                  <Label htmlFor="bulk-understood">I understand</Label>
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setBulk(null)}>Cancel</Button>
            <Button onClick={applyBulk} disabled={!choice || writable.length === 0 || (needsConfirm && !understood)}>
              Apply to {writable.length}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function errorReason(err: unknown): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  return err instanceof Error ? err.message : 'unknown error';
}

function FilterSelect({
  label, value, onChange, options,
}: { label: string; value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger aria-label={label} className="h-8 w-auto min-w-[8.5rem] text-xs"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{label}: all</SelectItem>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export default WorkspacesTable;
